import * as Headless from '@headlessui/react'
import { classNames } from '@/Utils/Utils'
import React from 'react'

// rounded border-gray-300 text-stone-500 shadow-sm focus:ring-stone-500
const base = [
    // Basic layout
    'relative isolate flex size-4 items-center justify-center rounded sm:size-4',
    // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
    'before:absolute before:inset-0 before:-z-10 before:rounded before:bg-white before:shadow',
    // Background color when checked
    'group-data-checked:before:bg-(--checkbox-checked-bg)',
    // Border
    'border border-stone-300 group-data-checked:border-transparent group-data-hover:group-data-checked:border-transparent group-data-hover:cursor-pointer group-data-checked:bg-(--checkbox-checked-border)',
    // Inner highlight shadow
    // Focus ring
    'group-data-focus:outline group-data-focus:outline-2 group-data-focus:outline-offset-2 group-data-focus:outline-blue-500',
    // Disabled state
    'group-data-disabled:opacity-50',
    'group-data-disabled:border-stone-950/25 group-data-disabled:bg-stone-950/5 group-data-disabled:[--checkbox-check:var(--color-stone-950)]/50 group-data-disabled:before:bg-transparent',
    // Forced colors mode
    'forced-colors:[--checkbox-check:HighlightText] forced-colors:[--checkbox-checked-bg:Highlight] forced-colors:group-data-disabled:[--checkbox-check:Highlight]',
    '[--checkbox-check:var(--color-white)] [--checkbox-checked-bg:var(--color-stone-500)] [--checkbox-checked-border:var(--color-stone-500)]',
]

export function Checkbox({
    className,
    ...props
}: {
    className?: string
} & Omit<Headless.CheckboxProps, 'className'>) {
    return (
        <Headless.Checkbox data-slot="control" {...props} className={classNames('group inline-flex focus:outline-hidden', className)}>
            <span className={classNames(base)}>
                <svg
                    className="size-4 stroke-(--checkbox-check) opacity-0 group-data-checked:opacity-100 sm:h-3.5 sm:w-3.5"
                    viewBox="0 0 14 14"
                    fill="none"
                >
                    {/* Checkmark icon */}
                    <path
                        className="opacity-100 group-data-indeterminate:opacity-0"
                        d="M3 8L6 11L11 3.5"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    {/* Indeterminate icon */}
                    <path
                        className="opacity-0 group-data-indeterminate:opacity-100"
                        d="M3 7H11"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
        </Headless.Checkbox>
    )
}

export function CheckboxGroup({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            data-slot="control"
            {...props}
            className={classNames(
                className,
                // Basic groups
                'space-y-3',
                // With descriptions
                'has-data-[slot=description]:space-y-6 has-data-[slot=description]:**:data-[slot=label]:font-medium'
            )}
        />
    )
}

export function CheckboxField({ className, ...props }: { className?: string } & Omit<Headless.FieldProps, 'as' | 'className'>) {
    return (
        <Headless.Field
            data-slot="field"
            {...props}
            className={classNames(
                className,
                // Base layout
                'grid grid-cols-[1.125rem_1fr] gap-x-2 gap-y-1 sm:grid-cols-[1rem_1fr]',
                // Control layout
                '*:data-[slot=control]:col-start-1 *:data-[slot=control]:row-start-1 *:data-[slot=control]:mt-0.75 sm:*:data-[slot=control]:mt-1',
                // Label layout
                '*:data-[slot=label]:col-start-2 *:data-[slot=label]:row-start-1',
                // Description layout
                '*:data-[slot=description]:col-start-2 *:data-[slot=description]:row-start-2',
                // With description
                'has-data-[slot=description]:**:data-[slot=label]:font-medium'
            )}
        />
    )
}
